// Port of SuiteModel/MapSketch.swift, operation for operation: the hand-drawn geometry of a map — lines as charcoal,
// outlines as pencil, regions as smudges. Each mark is worked out from the element's id (`SplitMix64` seeded with
// `<seed>|<part>`), so a map looks the same on every device and a redraw never jitters; the wobble is relative to the
// ends, so moving a note bends its lines without reshuffling them.
//
// The random draws come in the Swift's order, the constants are the Swift's (cos and sin of 0.45 written out), and only
// arithmetic, square roots, sine and cosine are used, so the two agree to well under a millionth of a point:
// `sketch.test.ts` writes fixtures/map-sketch/sketch.json, which SuiteModelTests/MapSketchTests.swift reads.
import { SplitMix64 } from '../random.ts';
import type { MapOutline, MapPoint } from './map.ts';

/** A point of a mark, with the stroke's width there (0 for a fill's outline). */
export interface SketchPoint { x: number; y: number; width: number }

export interface SketchMark {
  kind: 'stroke' | 'fill';
  points: SketchPoint[];
  /** The second pass of a line or outline is lighter. */
  opacity: number;
}

export interface SketchStyle {
  /** 1 = the usual wobble; 0 = steady lines (Settings ▸ Appearance), still drawn as pencil. */
  roughness: number;
  /** A line's width at its thickest, in points. */
  weight: number;
}

const DEFAULT_STYLE: SketchStyle = { roughness: 1, weight: 1.6 };

const random = (seed: string, part: string) => new SplitMix64(seed + '|' + part);

/** Swift's `.rounded(.toNearestOrAwayFromZero)`. */
const roundAway = (x: number) => Math.sign(x) * Math.round(Math.abs(x));

/** Thick in the middle, tapering at both ends: the pressure of a charcoal stroke. */
const taper = (t: number) => 0.3 + 0.7 * Math.sqrt(Math.max(0, 4 * t * (1 - t)));

const pt = (x: number, y: number, width: number): SketchPoint => ({ x, y, width });

export const MapSketch = {
  defaultStyle: DEFAULT_STYLE,
  steady: { roughness: 0, weight: 1.6 } as SketchStyle,

  // MARK: Lines

  /** A line from `a` to `b`: a cubic curve bowed a little to one side, drawn twice slightly apart. */
  line(a: MapPoint, b: MapPoint, seed: string, style: SketchStyle = DEFAULT_STYLE): SketchMark[] {
    return sketchLine(a, b, seed, style, 'free');
  },

  /** An arrowhead at `tip` for a line arriving from `from`: two short strokes. */
  arrowhead(tip: MapPoint, from: MapPoint, seed: string, style: SketchStyle = DEFAULT_STYLE): SketchMark[] {
    const dx = tip.x - from.x, dy = tip.y - from.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    if (!(length > 0.5)) return [];
    const vx = -dx / length, vy = -dy / length;
    // cos and sin of 0.45 radians, written out so both platforms use the same numbers.
    const c = 0.9004471023526769, s = 0.43496553411123023;
    const size = 7 + 2 * style.weight;
    const g = random(seed, 'arrow');
    const marks: SketchMark[] = [];
    for (const side of [-1, 1]) {
      const rx = vx * c - vy * s * side, ry = vx * s * side + vy * c;
      const reach = size * (1 + 0.15 * g.symmetric() * style.roughness);
      const ex = tip.x + rx * reach, ey = tip.y + ry * reach;
      const points: SketchPoint[] = [];
      for (let i = 0; i <= 4; i++) {
        const t = i / 4;
        points.push(pt(tip.x + (ex - tip.x) * t, tip.y + (ey - tip.y) * t, style.weight * (1 - 0.6 * t)));
      }
      marks.push({ kind: 'stroke', points, opacity: 0.9 });
    }
    return marks;
  },

  // MARK: Outlines

  /** A note's outline around its text box (`x`, `y`, `width`, `height`), drawn in pencil. */
  outline(shape: MapOutline, x: number, y: number, width: number, height: number, seed: string, style: SketchStyle = DEFAULT_STYLE): SketchMark[] {
    const pencil: SketchStyle = { roughness: style.roughness, weight: style.weight * 0.7 };
    switch (shape) {
      case 'none': return [];
      case 'oval': return oval(x, y, width, height, seed, pencil);
      case 'box': return box(x, y, width, height, seed, pencil);
      case 'cloud': return cloud(x, y, width, height, seed, pencil);
      case 'underline':
        // Drawn right to left so that bowing "outwards" (to its right) bends it away from the words, down.
        return sketchLine({ x: x + width + 6, y: y + height + 3 }, { x: x - 4, y: y + height + 5 }, seed + '|underline', pencil, 'outwards');
    }
  },

  /** The radii of an oval round a text box: tall enough to clear it by a little, and wide enough that its corners sit
   *  inside (an oval only just round a box cuts its corners). `extra` widens both (a cloud's bumps). */
  ovalRadii(width: number, height: number, extra = 0): { rx: number; ry: number } {
    const ry = height / 2 + 14 + extra;
    const k = height / 2 / ry;
    return { rx: (width / 2) / Math.sqrt(1 - k * k) + 6 + extra, ry };
  },

  // MARK: Regions

  /** A smudged wash filling roughly the rectangle: a closed shape with an uneven edge (the renderer softens and grains it). */
  region(x: number, y: number, width: number, height: number, seed: string, style: SketchStyle = DEFAULT_STYLE): SketchMark {
    const cx = x + width / 2, cy = y + height / 2;
    const rx = width / 2, ry = height / 2;
    const count = 28;
    const g = random(seed, 'region');
    const start = g.unit() * 2 * Math.PI / count;
    const radii: number[] = [];
    for (let i = 0; i < count; i++) radii.push(1 + 0.07 * g.symmetric() * style.roughness);
    const points: SketchPoint[] = [];
    for (let i = 0; i < count; i++) {
      const k = (radii[(i + count - 1) % count] + 2 * radii[i] + radii[(i + 1) % count]) / 4;
      const theta = start + 2 * Math.PI * i / count;
      points.push(pt(cx + rx * k * Math.cos(theta), cy + ry * k * Math.sin(theta), 0));
    }
    return { kind: 'fill', points, opacity: 1 };
  },
};

/** How far a stroke may bow: a connecting line either way (up to 6% of its length, 18 at most); an outline's side a
 *  little, and only outwards (to its right going round: 2%, 3 at most), so it never crosses the words inside. */
type Bowing = 'free' | 'outwards';

function sketchLine(a: MapPoint, b: MapPoint, seed: string, style: SketchStyle, bowing: Bowing): SketchMark[] {
  const dx = b.x - a.x, dy = b.y - a.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (!(length > 0.5)) return [];
  const ux = dx / length, uy = dy / length;
  const nx = -uy, ny = ux;
  const r = style.roughness;
  const shared = random(seed, 'line');
  const bow = bowing === 'free'
    ? shared.symmetric() * Math.min(length * 0.06, 18) * r
    : -Math.abs(shared.symmetric()) * Math.min(length * 0.02, 3) * r;
  const count = Math.max(8, Math.min(64, roundAway(length / 6)));
  const marks: SketchMark[] = [];
  for (let pass = 0; pass < 2; pass++) {
    const g = random(seed, `line${pass}`);
    const jitter = 1.4 * r;
    const s0 = g.symmetric(), s1 = g.symmetric(), s2 = g.symmetric(), s3 = g.symmetric();
    const s4 = g.symmetric(), s5 = g.symmetric();
    const p0x = a.x + nx * s0 * jitter + ux * s1 * jitter;
    const p0y = a.y + ny * s0 * jitter + uy * s1 * jitter;
    const p3x = b.x + nx * s2 * jitter + ux * s3 * jitter;
    const p3y = b.y + ny * s2 * jitter + uy * s3 * jitter;
    const wander = bowing === 'free' ? length * 0.015 : Math.min(length * 0.005, 1);
    const off1 = bow + s4 * wander * r;
    const off2 = bow + s5 * wander * r;
    const c1x = p0x + (p3x - p0x) * 0.33 + nx * off1;
    const c1y = p0y + (p3y - p0y) * 0.33 + ny * off1;
    const c2x = p0x + (p3x - p0x) * 0.67 + nx * off2;
    const c2y = p0y + (p3y - p0y) * 0.67 + ny * off2;
    const weight = style.weight * (pass === 0 ? 1 : 0.55);
    const points: SketchPoint[] = [];
    for (let i = 0; i <= count; i++) {
      const t = i / count, m = 1 - t;
      const x = m * m * m * p0x + 3 * m * m * t * c1x + 3 * m * t * t * c2x + t * t * t * p3x;
      const y = m * m * m * p0y + 3 * m * m * t * c1y + 3 * m * t * t * c2y + t * t * t * p3y;
      points.push(pt(x, y, weight * taper(t)));
    }
    marks.push({ kind: 'stroke', points, opacity: pass === 0 ? 0.9 : 0.5 });
  }
  return marks;
}

/** An oval traced twice, never closing exactly: the first pass runs past where it began, the second stops short. */
function oval(x: number, y: number, width: number, height: number, seed: string, style: SketchStyle): SketchMark[] {
  const cx = x + width / 2, cy = y + height / 2;
  const { rx, ry } = MapSketch.ovalRadii(width, height);
  const r = style.roughness;
  const count = 56;
  const marks: SketchMark[] = [];
  for (let pass = 0; pass < 2; pass++) {
    const g = random(seed, `oval${pass}`);
    const start = g.unit() * 2 * Math.PI;
    const sweep = pass === 0 ? 2 * Math.PI + 0.4 * r : 2 * Math.PI - 0.25 * r;
    const ax = rx * (1 + 0.04 * g.symmetric() * r);
    const ay = ry * (1 + 0.06 * g.symmetric() * r);
    const phase = g.unit() * 2 * Math.PI;
    const drift = pass === 0 ? 3 * r : -2 * r;
    const points: SketchPoint[] = [];
    for (let i = 0; i <= count; i++) {
      const t = i / count;
      const theta = start + sweep * t;
      const k = 1 + 0.025 * r * Math.sin(3 * theta + phase);
      const px = cx + (ax * k + drift * t) * Math.cos(theta);
      const py = cy + (ay * k + drift * t) * Math.sin(theta);
      points.push(pt(px, py, style.weight * (pass === 0 ? 1 : 0.6) * taper(t)));
    }
    marks.push({ kind: 'stroke', points, opacity: pass === 0 ? 0.9 : 0.5 });
  }
  return marks;
}

/** Four ruled sides, each running a little past its corners. */
function box(x: number, y: number, width: number, height: number, seed: string, style: SketchStyle): SketchMark[] {
  const left = x - 8, right = x + width + 8, top = y - 6, bottom = y + height + 6;
  const over = 4 * style.roughness;
  const sides: [MapPoint, MapPoint][] = [
    [{ x: left - over, y: top }, { x: right + over, y: top }],
    [{ x: right, y: top - over }, { x: right, y: bottom + over }],
    [{ x: right + over, y: bottom }, { x: left - over, y: bottom }],
    [{ x: left, y: bottom + over }, { x: left, y: top - over }],
  ];
  return sides.flatMap(([a, b], i) => sketchLine(a, b, `${seed}|side${i}`, style, 'outwards'));
}

/** Bumps round an oval, each a curve bulging outwards, as one stroke. */
function cloud(x: number, y: number, width: number, height: number, seed: string, style: SketchStyle): SketchMark[] {
  const cx = x + width / 2, cy = y + height / 2;
  const { rx, ry } = MapSketch.ovalRadii(width, height, 4);
  const r = style.roughness;
  const g = random(seed, 'cloud');
  const perimeter = Math.PI * (rx + ry);
  const bumps = Math.max(6, roundAway(perimeter / 44));
  const start = g.unit() * 2 * Math.PI / bumps;
  const corners: [number, number][] = [];
  for (let i = 0; i < bumps; i++) {
    const theta = start + 2 * Math.PI * i / bumps;
    corners.push([cx + rx * Math.cos(theta), cy + ry * Math.sin(theta)]);
  }
  const points: SketchPoint[] = [];
  for (let i = 0; i < bumps; i++) {
    const [ax, ay] = corners[i], [bx, by] = corners[(i + 1) % bumps];
    const mx = (ax + bx) / 2, my = (ay + by) / 2;
    const ox = mx - cx, oy = my - cy;
    const out = Math.sqrt(ox * ox + oy * oy);
    const chord = Math.sqrt((bx - ax) * (bx - ax) + (by - ay) * (by - ay));
    const lift = chord * 0.45 * (1 + 0.15 * g.symmetric() * r);
    const qx = mx + (out > 0 ? ox / out : 0) * lift, qy = my + (out > 0 ? oy / out : 0) * lift;
    for (let j = i === 0 ? 0 : 1; j <= 6; j++) {
      const t = j / 6, m = 1 - t;
      const px = m * m * ax + 2 * m * t * qx + t * t * bx;
      const py = m * m * ay + 2 * m * t * qy + t * t * by;
      points.push(pt(px, py, style.weight * (0.55 + 0.45 * taper(t))));
    }
  }
  return [{ kind: 'stroke', points, opacity: 0.85 }];
}
